import { useEffect, useState } from 'react';
import { formatPrice } from '@fantasy/types';
import { api, ApiRequestError, type PlayerSearchResult } from '../api.ts';

const DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;

/** The shape a picker hands back — real data from our ingested player pool,
 * never free text, so a squad slot can't disagree with a player's actual
 * club, position or price. Structurally the same as `PlayerSearchResult`,
 * kept separate so callers aren't forced to import the API's search type. */
export interface PlayerPickerValue {
  fplId: number;
  name: string;
  position: string;
  club: string;
  price: number;
}

interface PlayerPickerProps {
  /** Currently selected player, or null for an empty slot. */
  value: PlayerPickerValue | null;
  onChange: (player: PlayerPickerValue | null) => void;
  /** Accessible label for this slot, e.g. "Goalkeeper" or "Bench 2". */
  label: string;
}

type Status = 'idle' | 'searching' | 'error';

/**
 * A labelled typeahead over the real player pool. Free-text club/position/
 * price entry has no connection to our ingested data, so a wrong club could
 * be typed and silently accepted as fact — this makes that structurally
 * impossible by only ever handing back a player that came from a search
 * result. Self-contained: no knowledge of squads, constraints or captains.
 */
export function PlayerPicker({ value, onChange, label }: PlayerPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PlayerSearchResult[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  // Debounced search: a fresh timer is scheduled on every keystroke and the
  // previous one is cleared, so only the last keystroke in a burst actually
  // fires a request. A query under the minimum length never searches at all,
  // client-side, rather than relying on the server's short-circuit.
  useEffect(() => {
    if (!isOpen) return;

    if (query.trim().length < MIN_QUERY_LENGTH) {
      setResults([]);
      setStatus('idle');
      setError(null);
      return;
    }

    setStatus('searching');
    setError(null);

    const timer = setTimeout(async () => {
      try {
        const response = await api.searchPlayers(query.trim());
        setResults(response.players);
        setStatus('idle');
      } catch (err) {
        setError(err instanceof ApiRequestError ? err.message : 'Could not search for players.');
        setStatus('error');
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, isOpen]);

  function openSearch() {
    setIsOpen(true);
    setQuery('');
    setResults([]);
    setStatus('idle');
    setError(null);
  }

  function selectPlayer(player: PlayerSearchResult) {
    onChange(player);
    setIsOpen(false);
    setQuery('');
    setResults([]);
  }

  const inputId = `player-picker-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const showDropdown = isOpen && (status !== 'idle' || query.trim().length >= MIN_QUERY_LENGTH);

  return (
    <div className="player-picker">
      <label htmlFor={inputId}>{label}</label>
      <input
        id={inputId}
        type="text"
        placeholder={label}
        value={isOpen ? query : value ? `${value.name} · ${value.club}` : ''}
        onChange={isOpen ? (e) => setQuery(e.target.value) : undefined}
        onClick={!isOpen ? openSearch : undefined}
        readOnly={!isOpen}
      />

      {showDropdown && (
        <div className="panel player-picker-menu">
          {status === 'searching' && <p className="hint">Searching…</p>}

          {status === 'error' && (
            <ul className="notices notices--flush">
              <li className="error">{error}</li>
            </ul>
          )}

          {status === 'idle' && results.length === 0 && query.trim().length >= MIN_QUERY_LENGTH && (
            <p className="hint">No players found.</p>
          )}

          {status === 'idle' && results.length > 0 && (
            <ul className="notices notices--flush">
              {results.map((player) => (
                <li key={player.fplId}>
                  <button
                    type="button"
                    className="ghost small player-picker-option"
                    onClick={() => selectPlayer(player)}
                  >
                    <span>{player.name}</span>
                    <span className="hint hint--inline">
                      {player.position} · {player.club} · {formatPrice(player.price)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
